import React, { useState, useMemo } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import SidePanel from "../common/layout/SidePanel";
import { getActivityView } from "../../api/process";
import ActivityView from "./ActivityView";




export default function TreeTable({ treeData = [] }) {
    const [openNodes, setOpenNodes] = useState(new Set());
    const [showOnlyCP, setShowOnlyCP] = useState(false);

    const [selectedActivity, setSelectedActivity] = useState(null);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [ganttData, setGanttData] = useState([]);


    const statusMap = {
        NORMAL: { label: "-", color: "bg-gray-100 text-gray-600" },
        DELAY: { label: "지연", color: "bg-red-100 text-red-600" },
        DONE: { label: "완료", color: "bg-green-100 text-green-600" },
        EARLY: { label: "선행", color: "bg-blue-100 text-blue-600" },
    };

    // 하위 활동 수집 (집계용)
    const activityMap = useMemo(() => {
        const map = new Map();

        const walk = (node) => {
            let acts = [...(node.activities || [])];
            (node.children || []).forEach((c) => {
                acts = acts.concat(walk(c));
            });
            map.set(node.key, acts);
            return acts;
        };

        treeData.forEach(walk);
        return map;
    }, [treeData]);

    const toggleNode = (key) => {
        const next = new Set(openNodes);
        next.has(key) ? next.delete(key) : next.add(key);
        setOpenNodes(next);
    };

    const toggleAll = (expand) => {
        if (expand) {
            // 모든 key 수집
            const collectKeys = (nodes) => {
                let keys = [];
                nodes.forEach((n) => {
                    keys.push(n.key);
                    keys = keys.concat(collectKeys(n.children || []));
                });
                return keys;
            };
            const allKeys = collectKeys(treeData);
            setOpenNodes(new Set(allKeys));
        } else {
            setOpenNodes(new Set()); // 전부 닫기
        }
    };

    function filterCPNodes(nodes, showOnlyCP) {
        if (!showOnlyCP) return nodes;

        return nodes
            .map((node) => {
                // 자식 WBS도 재귀 처리
                const filteredChildren = filterCPNodes(node.children || [], showOnlyCP);
                // Activity 중 CP만 필터링
                const filteredActs = (node.activities || []).filter((act) => act.is_cp);

                // 현재 WBS에서 CP Activity나 CP 자식이 하나라도 있으면 남긴다
                if (filteredChildren.length > 0 || filteredActs.length > 0) {
                    return {
                        ...node,
                        activities: filteredActs,
                        children: filteredChildren,
                    };
                }

                // CP가 전혀 없으면 숨김
                return null;
            })
            .filter(Boolean);
    }



    const visibleData = filterCPNodes(treeData, showOnlyCP);

    const renderNode = (node, depth = 0) => {
        const isOpen = openNodes.has(node.key);

        // ✅ 그룹 집계
        const allActs = activityMap.get(node.key) || [];

        const minStart = allActs.map((a) => a.start_date).filter(Boolean).sort()[0];
        const maxFinish = allActs
            .map((a) => a.finish_date)
            .filter(Boolean)
            .sort()
            .reverse()[0];

        const minBaselineStart = allActs
            .map((a) => a.baseline_start_date)
            .filter(Boolean)
            .sort()[0];
        const maxBaselineFinish = allActs
            .map((a) => a.baseline_finish_date)
            .filter(Boolean)
            .sort()
            .reverse()[0];

        const totalPlan = allActs.reduce(
            (sum, a) => sum + (Number(a.plan_labor) || 0),
            0
        );
        const totalActual = allActs.reduce(
            (sum, a) => sum + (Number(a.actual_labor) || 0),
            0
        );
        const percent =
            totalPlan > 0 ? Math.min(Math.round((totalActual / totalPlan) * 100), 100) : 0;

        const depthColors = [
            "border-blue-400",
            "border-green-400",
            "border-purple-400",
            "border-pink-400",
        ];

        const depthBg = [
            "bg-gray-100",
            "bg-gray-200",
            "bg-gray-300",
            "bg-gray-400",
        ];



        return (
            <React.Fragment key={node.key}>
                {/* 그룹 행 */}
                <tr
                    className={`hover:bg-gray-100 cursor-pointer bg-gray-100`}
                    onClick={() => toggleNode(node.key)}
                >
                    <td
                        className={`px-2 py-1.5 text-left text-gray-800 font-medium border-l-4 ${depthColors[depth % depthColors.length]}`}
                        style={{ paddingLeft: `${depth * 20}px` }}
                    >
                        <div className="flex items-center gap-1">
                            {isOpen ? (
                                <ChevronDown className="w-3 h-3 text-gray-600" />
                            ) : (
                                <ChevronRight className="w-3 h-3 text-gray-600" />
                            )}
                            <span className="text-xs">{node.name}</span>
                        </div>
                    </td>
                    <td className="px-2 py-1.5 text-xs"></td>
                    {/* <td className="px-2 py-1.5 text-xs"></td> */}
                    {/* <td className="px-2 py-1.5 text-xs text-center">{minStart?.slice(0, 10) || "-"}</td> */}
                    {/* <td className="px-2 py-1.5 text-xs text-center">{maxFinish?.slice(0, 10) || "-"}</td> */}
                    {/* <td className="px-2 py-1.5 text-xs text-center">{minBaselineStart || "-"}</td> */}
                    {/* <td className="px-2 py-1.5 text-xs text-center">{maxBaselineFinish || "-"}</td> */}
                    <td className="px-2 py-1.5 text-xs text-center">
                        {Math.round(totalActual).toLocaleString()} / {Math.round(totalPlan).toLocaleString()}
                    </td>
                    <td className="px-2 py-1.5 text-xs">
                        <div className="flex items-center gap-2">
                            <div className="w-full bg-gray-200 rounded h-2">
                                <div
                                    className="h-2 rounded bg-blue-500"
                                    style={{ width: `${percent}%` }}
                                />
                            </div>
                            <span className="text-[10px] text-gray-600 w-10 text-right">
                                {percent}%
                            </span>
                        </div>
                    </td>
                    <td className="px-2 py-1.5 text-xs"></td>
                </tr>

                {/* 활동 행 */}
                {isOpen &&
                    node.activities.map((act) => {
                        const plan = Number(act.plan_labor) || 0;
                        const actual = Number(act.actual_labor) || 0;
                        const percent =
                            plan > 0 ? Math.min(Math.round((actual / plan) * 100), 100) : 0;
                        const percentText =
                            !isNaN(percent) && percent > 0 ? percent : act.percent_complete;

                        let barColor = "bg-green-500";
                        if (act.status === "delay") barColor = "bg-red-500";
                        if (act.status === "done") barColor = "bg-blue-500";

                        return (
                            <tr
                                key={act.id}
                                className="border-t hover:bg-gray-50 text-xs text-gray-700"
                                onClick={async () => {
                                    setSelectedActivity(act);
                                    setIsPanelOpen(true);

                                    const res = await getActivityView(act.id);
                                    setGanttData(res);
                                }}

                            >
                                {/* Activity Name */}
                                <td className="px-2 py-1.5 font-medium text-gray-800">
                                    <div className="flex items-center gap-1" style={{ marginLeft: (depth + 1) * 20 }}>
                                        <span className={act.is_cp ? "text-red-500 font-semibold" : ""}>
                                            {act.name}
                                        </span>
                                        <span className="text-gray-400 text-xs">({act.activity_code})</span>
                                    </div>
                                </td>
                                <td className={act.is_cp ? "px-2 py-1.5 text-center  text-red-500" : "px-2 py-1.5 text-center"}>{act.total_float == null ? "-" : act.total_float}</td>

                                {/* Dates */}
                                {/* <td className="px-2 py-1.5 text-center">{act.start_date?.slice(0, 10)}</td> */}
                                {/* <td className="px-2 py-1.5 text-center">{act.finish_date?.slice(0, 10)}</td> */}
                                {/* <td className="px-2 py-1.5 text-center">{act.baseline_start_date}</td> */}
                                {/* <td className="px-2 py-1.5 text-center">{act.baseline_finish_date}</td> */}

                                {/* Labor */}
                                <td className="px-2 py-1.5 text-center">
                                    {Math.round(Number(act.actual_labor)).toLocaleString()} / {Math.round(Number(act.plan_labor)).toLocaleString()}
                                </td>

                                {/* Progress */}
                                <td className="px-2 py-1.5">
                                    <div className="flex items-center gap-2">
                                        <div className="w-full bg-gray-200 rounded h-2">
                                            <div
                                                className={`h-2 rounded ${barColor}`}
                                                style={{ width: `${percentText}%` }}
                                            />
                                        </div>
                                        <span className="text-[10px] text-gray-600 w-10 text-right">
                                            {percentText}%
                                        </span>
                                    </div>
                                </td>

                                {/* Status */}
                                <td className="px-2 py-1.5 text-center">
                                    {(() => {
                                        const s = statusMap[act.status] || { label: act.status, color: "bg-gray-100 text-gray-600" };
                                        return (
                                            <span
                                                className={`px-2 py-0.5 rounded text-[10px] font-medium ${s.color}`}
                                            >
                                                {s.label}
                                            </span>
                                        );
                                    })()}
                                </td>
                            </tr>
                        );
                    })}


                {/* 하위 그룹 */}
                {isOpen &&
                    node.children.map((child) => renderNode(child, depth + 1))}
            </React.Fragment>
        );
    };

    return (
        <div className="overflow-x-auto rounded-2xl border bg-white">
            {/* 상단에 버튼 영역 */}
            <div className="flex justify-end gap-2 p-2">
                <input
                    type="checkbox"
                    checked={showOnlyCP}
                    onChange={(e) => setShowOnlyCP(e.target.checked)}
                />
                <label className="text-sm">Critical Path</label>
                <button
                    onClick={() => toggleAll(true)}
                    className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                    모두 펼치기
                </button>
                <button
                    onClick={() => toggleAll(false)}
                    className="px-3 py-1 text-xs bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                >
                    모두 닫기
                </button>
            </div>
            <table className="min-w-full text-sm table-fixed">
                <thead className="bg-gray-50 text-gray-600">
                    <tr>
                        {/* <th className="px-2 py-1.5 text-left w-20">공정 ID</th> */}
                        <th className="px-2 py-1.5 text-left w-64">공정명(공정 ID)</th>
                        <th className="px-2 py-1.5 text-center w-20">total float</th>
                        {/* <th className="px-2 py-1.5 text-center w-20">시작일</th> */}
                        {/* <th className="px-2 py-1.5 text-center w-20">완료일</th> */}
                        {/* <th className="px-2 py-1.5 text-center w-20">예정시작일</th> */}
                        {/* <th className="px-2 py-1.5 text-center w-20">예정완료일</th> */}
                        <th className="px-2 py-1.5 text-center w-28">실적</th>
                        <th className="px-2 py-1.5 text-center w-24">진도율</th>
                        <th className="px-2 py-1.5 text-center w-16">상태</th>
                    </tr>
                </thead>
                <tbody>{visibleData.map((n) => renderNode(n))}</tbody>
            </table>
            <SidePanel
                isOpen={isPanelOpen}
                onClose={() => setIsPanelOpen(false)}
                title={selectedActivity?.name}
                width={1000}
                side="right"
            >
                {selectedActivity && (
                    <ActivityView activity={selectedActivity} ganttData={ganttData} />
                )}
            </SidePanel>
        </div>
    );
}
